/**
 * Morph Deformation Engine
 * 
 * Core deformation system that applies morph channel values to mesh vertices.
 * Implements weighted vertex influence, smooth falloff, and corrective stabilization.
 * 
 * Architecture follows Blender Shape Keys / Blend Shapes principles:
 * - Each morph channel defines a region and deformation axis
 * - Vertices within region receive weighted displacement
 * - Gaussian/smooth falloff prevents sharp edges
 * - Corrective smoothing stabilizes the final result
 */

import * as THREE from 'three';
import { MORPH_CHANNELS, getChannelById } from './MorphChannelDefinitions';

/**
 * MorphDeformationEngine class
 * 
 * Handles all vertex-level deformation calculations.
 */
export class MorphDeformationEngine {
  constructor() {
    this.channels = MORPH_CHANNELS;
    this.vertexWeightCache = new Map();
    this.modelBounds = null;
    this.originalPositions = null;
    this.smoothingIterations = 4; // Increased from 2 for smoother results
  }

  /**
   * Initialize the engine with mesh data
   * 
   * @param {THREE.BufferGeometry} geometry - The mesh geometry
   */
  initialize(geometry) {
    if (!geometry || !geometry.attributes.position) {
      console.error('Invalid geometry provided to MorphDeformationEngine');
      return false;
    }

    const positions = geometry.attributes.position;
    
    // Store original positions
    this.originalPositions = new Float32Array(positions.array.length);
    this.originalPositions.set(positions.array);

    // Calculate model bounds
    geometry.computeBoundingBox();
    this.modelBounds = {
      min: geometry.boundingBox.min.clone(),
      max: geometry.boundingBox.max.clone(),
      center: new THREE.Vector3(),
      size: new THREE.Vector3()
    };
    geometry.boundingBox.getCenter(this.modelBounds.center);
    geometry.boundingBox.getSize(this.modelBounds.size);

    // Pre-calculate vertex weights for each channel
    this.precomputeVertexWeights(positions);

    console.log('MorphDeformationEngine initialized:', {
      vertexCount: positions.count,
      bounds: this.modelBounds
    });

    return true;
  }

  /**
   * Pre-compute vertex weights for all morph channels
   * 
   * This is done once at initialization to avoid per-frame calculations.
   */
  precomputeVertexWeights(positions) {
    this.vertexWeightCache.clear();

    for (const [channelId, channel] of Object.entries(this.channels)) {
      const weights = new Float32Array(positions.count);
      
      for (let i = 0; i < positions.count; i++) {
        const x = positions.getX(i);
        const y = positions.getY(i);
        const z = positions.getZ(i);
        
        weights[i] = this.calculateVertexWeight(channel, x, y, z);
      }
      
      this.vertexWeightCache.set(channelId, weights);
    }
  }

  /**
   * Calculate weight for a single vertex based on channel region
   */
  calculateVertexWeight(channel, x, y, z) {
    const { region, influence } = channel;
    const bounds = this.modelBounds;
    
    // Normalize vertex position relative to model bounds (-1 to 1)
    const nx = (x - bounds.center.x) / (bounds.size.x * 0.5);
    const ny = (y - bounds.center.y) / (bounds.size.y * 0.5);
    const nz = (z - bounds.center.z) / (bounds.size.z * 0.5);

    let weight = 0;

    switch (region.type) {
      case 'full':
        weight = 1.0;
        break;

      case 'radial_from_axis':
        // Weight based on distance from specified axis
        if (region.axis === 'y') {
          weight = Math.sqrt(nx * nx + nz * nz); // Distance from Y axis
        } else if (region.axis === 'x') {
          weight = Math.sqrt(ny * ny + nz * nz);
        } else {
          weight = Math.sqrt(nx * nx + ny * ny);
        }
        weight = Math.min(1, weight);
        break;

      case 'hemisphere':
        // Upper or lower hemisphere
        if (region.direction === 'upper') {
          weight = ny > region.threshold ? this.smoothstep(region.threshold, 1, ny) : 0;
        } else {
          weight = ny < -region.threshold ? this.smoothstep(-1, -region.threshold, -ny) : 0;
        }
        break;

      case 'sphere':
        weight = this.calculateSphereWeight(nx, ny, nz, region.center, region.radius);
        break;

      case 'dual_sphere':
        // Maximum weight from either sphere (for paired features like eyes)
        const w1 = this.calculateSphereWeight(nx, ny, nz, region.centers[0], region.radius);
        const w2 = this.calculateSphereWeight(nx, ny, nz, region.centers[1], region.radius);
        weight = Math.max(w1, w2);
        break;

      case 'box':
        weight = this.calculateBoxWeight(nx, ny, nz, region.center, region.size);
        break;

      case 'face_front':
        // Front-facing vertices (positive Z) with y-based falloff
        if (nz > region.zThreshold) {
          const zFactor = this.smoothstep(region.zThreshold, 1, nz);
          weight = zFactor;
        }
        break;

      default:
        weight = 0;
    }

    // Apply influence falloff
    if (region.falloff === 'gaussian') {
      weight = this.gaussianFalloff(weight, influence.spread);
    } else if (region.falloff === 'smooth') {
      weight = this.smoothstep(0, 1, weight);
    }

    return weight * influence.spread;
  }

  /**
   * Calculate weight for sphere region
   */
  calculateSphereWeight(x, y, z, center, radius) {
    const dx = x - center.x;
    const dy = y - center.y;
    const dz = z - center.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    
    if (dist > radius) return 0;
    return 1 - (dist / radius);
  }

  /**
   * Calculate weight for box region
   */
  calculateBoxWeight(x, y, z, center, size) {
    const dx = Math.abs(x - center.x) / (size.x * 0.5);
    const dy = Math.abs(y - center.y) / (size.y * 0.5);
    const dz = Math.abs(z - center.z) / (size.z * 0.5);
    
    if (dx > 1 || dy > 1 || dz > 1) return 0;
    
    // Weight is minimum distance from box surface (inverted)
    const maxDist = Math.max(dx, dy, dz);
    return 1 - maxDist;
  }

  /**
   * Gaussian falloff function
   */
  gaussianFalloff(weight, sigma) {
    return Math.exp(-((1 - weight) * (1 - weight)) / (2 * sigma * sigma));
  }

  /**
   * Smoothstep interpolation
   */
  smoothstep(edge0, edge1, x) {
    const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
    return t * t * (3 - 2 * t);
  }

  /**
   * Apply morph channel values to deform the mesh
   * 
   * @param {THREE.BufferAttribute} positions - The position attribute to modify
   * @param {Object} morphValues - Object mapping channel IDs to values (-1 to 1)
   * @returns {boolean} Success status
   */
  applyDeformation(positions, morphValues) {
    if (!this.originalPositions || !positions) {
      console.error('MorphDeformationEngine not initialized or invalid positions');
      return false;
    }

    const count = positions.count;
    
    // Create displacement accumulator
    const displacements = new Float32Array(count * 3);

    // Accumulate displacements from all channels
    for (const [channelId, value] of Object.entries(morphValues)) {
      if (Math.abs(value) < 0.001) continue; // Skip negligible values
      
      const channel = this.channels[channelId];
      const weights = this.vertexWeightCache.get(channelId);
      
      if (!channel || !weights) continue;

      this.accumulateChannelDisplacement(displacements, weights, channel, value, count);
    }

    // Apply corrective smoothing
    this.applyCorrectiveSmoothing(displacements, count);

    // Apply displacements to original positions
    for (let i = 0; i < count; i++) {
      const ox = this.originalPositions[i * 3];
      const oy = this.originalPositions[i * 3 + 1];
      const oz = this.originalPositions[i * 3 + 2];
      
      positions.setXYZ(
        i,
        ox + displacements[i * 3],
        oy + displacements[i * 3 + 1],
        oz + displacements[i * 3 + 2]
      );
    }

    positions.needsUpdate = true;
    return true;
  }

  /**
   * Accumulate displacement for a single channel
   */
  accumulateChannelDisplacement(displacements, weights, channel, value, count) {
    const { axis, range } = channel;
    
    // Scale value to channel's range
    const scaledValue = value >= 0 
      ? value * range.max 
      : value * Math.abs(range.min);

    for (let i = 0; i < count; i++) {
      const weight = weights[i];
      if (weight < 0.001) continue;

      const displacement = scaledValue * weight;
      const idx = i * 3;

      switch (axis) {
        case 'x':
          displacements[idx] += displacement;
          break;
        case 'y':
          displacements[idx + 1] += displacement;
          break;
        case 'z':
          displacements[idx + 2] += displacement;
          break;
        case 'uniform':
          // Radial displacement from center
          const ox = this.originalPositions[idx];
          const oy = this.originalPositions[idx + 1];
          const oz = this.originalPositions[idx + 2];
          const cx = this.modelBounds.center.x;
          const cy = this.modelBounds.center.y;
          const cz = this.modelBounds.center.z;
          const dx = ox - cx;
          const dy = oy - cy;
          const dz = oz - cz;
          const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (len > 0.001) {
            displacements[idx] += (dx / len) * displacement;
            displacements[idx + 1] += (dy / len) * displacement;
            displacements[idx + 2] += (dz / len) * displacement;
          }
          break;
        case 'x_outward':
          // Move away from center on X axis
          const sign = this.originalPositions[idx] >= this.modelBounds.center.x ? 1 : -1;
          displacements[idx] += displacement * sign;
          break;
      }
    }
  }

  /**
   * Apply corrective smoothing to prevent ugly deformations
   * 
   * Uses a simple Laplacian-like smoothing approach
   */
  applyCorrectiveSmoothing(displacements, count) {
    // Skip if no smoothing needed
    if (this.smoothingIterations <= 0) return;

    // For performance, we do a simplified vertex relaxation
    // This is NOT a full Laplacian smooth, but a lighter version
    
    const temp = new Float32Array(count * 3);
    const dampingFactor = 0.35; // Increased from 0.2 for stronger smoothing

    for (let iter = 0; iter < this.smoothingIterations; iter++) {
      // Copy current displacements
      temp.set(displacements);

      for (let i = 0; i < count; i++) {
        const idx = i * 3;
        
        // Get displacement magnitude
        const dx = displacements[idx];
        const dy = displacements[idx + 1];
        const dz = displacements[idx + 2];
        const mag = Math.sqrt(dx * dx + dy * dy + dz * dz);
        
        // Apply damping to large displacements - lowered threshold
        if (mag > 0.03) {
          const damping = 1 - (dampingFactor * (mag - 0.03) / mag);
          temp[idx] *= damping;
          temp[idx + 1] *= damping;
          temp[idx + 2] *= damping;
        }
      }

      // Copy back
      displacements.set(temp);
    }
  }

  /**
   * Reset to original positions
   */
  reset(positions) {
    if (!this.originalPositions || !positions) return;

    for (let i = 0; i < positions.count; i++) {
      positions.setXYZ(
        i,
        this.originalPositions[i * 3],
        this.originalPositions[i * 3 + 1],
        this.originalPositions[i * 3 + 2]
      );
    }
    
    positions.needsUpdate = true;
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.vertexWeightCache.clear();
    this.originalPositions = null;
    this.modelBounds = null;
  }
}

// Export singleton factory
export const createMorphDeformationEngine = () => new MorphDeformationEngine();

export default MorphDeformationEngine;
